/**
 * Autore: Salvo de Luca
 * Azienda: Dialogicom s.r.l.s.
 * File: components/templates/AdminPageLayout.tsx
 * Descrizione: Impalcatura comune delle pagine di Amministrazione —
 *              header fisso, menù di navigazione e footer.
 *
 * Nasce per non ripetere lo stesso blocco in ogni pagina: prima ciascuna se lo
 * copiava (o se lo dimenticava), e infatti header, menù e footer erano presenti
 * solo in alcune, con misure leggermente diverse. Le costanti di ingombro stanno
 * qui una volta sola: se cambia l'altezza dell'header si aggiorna un punto solo.
 *
 * Non usa AppLayout (components/templates/AppLayout.tsx): quello monta la sidebar
 * ed e' pensato per l'area profilo, mentre qui serve il menu orizzontale con i
 * badge, lo stesso della Dashboard.
 */

import React from 'react';
import { useAuth } from '../auth/AuthProvider';
import { useAuthActions } from '../auth/useAuthActions';
import CustomHeader from '../ui-kit-dialogicom/inputs/CustomHeader';
import NavigationMenuWithBadges from '../navigation/NavigationMenuWithBadges';
import Footer from '../ui-kit-dialogicom/layouts/Footer';
import { mainHeaderConfig } from '../../config/headerConfig';
import { getHeaderExtras } from '../../config/headerExtras';

/** Header (80px) + menù (~56px) + aria: e' il padding che il contenuto deve lasciare. */
const INGOMBRO_SUPERIORE = 150;
const LARGHEZZA_MASSIMA = 1400;

interface Props {
  children: React.ReactNode;
  /** Larghezza massima del contenuto; default 1400px come la Dashboard. */
  maxWidth?: number;
  /** Per pagine che gestiscono da sole il padding orizzontale. */
  senzaPaddingLaterale?: boolean;
}

export default function AdminPageLayout({ children, maxWidth = LARGHEZZA_MASSIMA, senzaPaddingLaterale = false }: Props) {
  const { user } = useAuth();
  const { signOut } = useAuthActions();

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white">
      <div className="fixed top-0 left-0 right-0 z-50">
        <CustomHeader
          {...mainHeaderConfig}
          logout={{
            enabled: true,
            useRealAuth: true,
            showCountdown: true,
            loginUrl: '/login',
            onLogoutClick: signOut,
            labels: { loggedIn: 'Logout', loggedOut: 'Login' },
          }}
          extraFixed={getHeaderExtras(user)}
        />
      </div>

      <div className="fixed left-0 right-0 z-40" style={{ top: '80px' }}>
        <div style={{ width: '100%', maxWidth: `${LARGHEZZA_MASSIMA}px`, marginLeft: 'auto', marginRight: 'auto' }}>
          <NavigationMenuWithBadges />
        </div>
      </div>

      <div
        className={senzaPaddingLaterale ? 'py-6' : 'px-4 sm:px-6 lg:px-8 py-6'}
        style={{
          width: '100%',
          maxWidth: `${maxWidth}px`,
          marginLeft: 'auto',
          marginRight: 'auto',
          paddingTop: `${INGOMBRO_SUPERIORE}px`,
        }}
      >
        {children}
      </div>

      <Footer />
    </div>
  );
}
